import * as React from 'react';
import {useDispatch, useSelector, useStore} from 'react-redux';

import classnames from 'classnames';
import {change, Field, FormSection, formValueSelector, getFormValues} from 'redux-form';

import type {
    EmailAddress as EmailAddressFragment,
    SessionUser as SessionUserFragment,
    EmailableLead,
    GetSuggestedReplies_suggestedReplies as SuggestedReplies,
} from 'nutshell-graphql-types';

import type {NutshellState} from '../../../store';

import {EmailRelatedLeadContext} from '../email-related-lead-context';
import type {TemplateGroup} from '../templates/template-groups/types';

import {EmailFormSection} from './fields/email-form-section';
import {FromField} from './fields/from-field';
import {RecipientsField} from './fields/recipients-field';
import {RelatedLeadsField} from './fields/related-leads-field';
import {ReplyButtonField} from './fields/reply-button-field';
import {flattenTemplatesInSequence} from './flatten-templates-in-sequence';
import {getTemplateFormStateName} from './get-template-form-state-name';
import {
    appendReplyQuotedSuffixToSequenceState,
    getMaterializationEntityIdFromToAddresses,
    getReplyMessageTemplateIds,
} from './helpers';
import {modifyReplySubjects} from './modify-reply-subjects';
import {getGroupSequenceTemplates} from './sender-with-groups/helpers';
import {CREATE_FROM_SCRATCH_ID, FORM_NAME} from './types';
import type {
    EmailComposerFormSequenceState,
    InexactMaterializedEmailSequenceFragment,
} from './types';
import {useGetMaterializedSequenceTemplate} from './use-get-materialized-sequence-template';

import styles from './email-composer-form.module.css';

const valueSelector = formValueSelector(FORM_NAME);

type Props = {
    user: SessionUserFragment;
    emails: EmailAddressFragment[];
    relatedLeads: EmailableLead[];
    isMultiSequence: boolean;
    isBodyFocused?: boolean;
    selectedSequenceId: string;
    selectedTemplateId: string;
    resetSelectedSequence: () => void;
    onChangeBody: (arg1: {plaintext: string; html: string}) => void;
    onChangeSubject: (arg1: string) => void;
    emailReplyId?: string;
    suggestedReplies?: SuggestedReplies;
    selectedSuggestedReplyId?: string;
    isLoadingSuggestedReplies?: boolean;
    /** HTML to append to the first materialized email body in reply mode (quoted thread + signature). */
    replyQuotedSuffixHtml?: string | null;
};

export function EmailComposerForm(props: Props) {
    const [materializedSequenceIds, setMaterializedSequenceIds] = React.useState<string[]>([]);
    const [replyTemplateIds, setReplyTemplateIds] = React.useState<string[]>([]);
    const [showCC, setShowCC] = React.useState<boolean>(false);
    const dispatch = useDispatch();
    const store = useStore<NutshellState>();
    const sequences: {
        [sequenceId: string]: EmailComposerFormSequenceState;
    } = useSelector((state: NutshellState) => valueSelector(state, 'sequences'));
    const selectedSequence = sequences[props.selectedSequenceId];
    if (!selectedSequence) props.resetSelectedSequence();

    // Safety in case we haven't cleared the selectedSequenceId yet but that sequence isn't in the search results
    const templateIds = selectedSequence ? Object.keys(selectedSequence.emailTemplates) : [];

    const toAddressesValue = useSelector((state: NutshellState) =>
        valueSelector(state, 'toAddresses')
    );

    const materializationEntityId = getMaterializationEntityIdFromToAddresses(
        toAddressesValue,
        props.relatedLeads[0]?.id
    );
    const reply = useSelector((state: NutshellState) => valueSelector(state, 'reply'));
    const isReply = Boolean(reply);
    const isReplyAll = Boolean(reply && reply.isReplyAll);
    // We only want to allow the user to switch to reply all if there are > 1 emails.
    const shouldShowReplyAllSwitch = props.emails.length > 1;

    const {materializedSequenceTemplate, isMaterializing} = useGetMaterializedSequenceTemplate(
        props.selectedSequenceId,
        materializationEntityId
    );

    React.useEffect(() => {
        const newSequence = materializedSequenceTemplate;

        const onSequenceMaterialize = (sequence: InexactMaterializedEmailSequenceFragment) => {
            const currentValues = getFormValues(FORM_NAME)(store.getState());
            if (!currentValues) {
                return;
            }

            const flattenedSequence = flattenTemplatesInSequence(sequence);
            let sequenceWithSubjects = modifyReplySubjects(flattenedSequence);
            if (props.replyQuotedSuffixHtml) {
                sequenceWithSubjects = appendReplyQuotedSuffixToSequenceState(
                    sequenceWithSubjects,
                    props.replyQuotedSuffixHtml
                );
            }
            // Merge into current `sequences`, not getFormInitialValues().sequences — the new
            // template sidebar loads many sequences via change(); initialize() would wipe them.
            dispatch(
                change(FORM_NAME, 'sequences', {
                    // @ts-expect-error redux-form values
                    ...currentValues.sequences,
                    [sequence.emailSequenceTemplate.id]: sequenceWithSubjects,
                })
            );
        };

        if (!newSequence) {
            if (props.selectedSequenceId === CREATE_FROM_SCRATCH_ID) {
                setMaterializedSequenceIds([]);
                setReplyTemplateIds([]);
            }
        } else {
            const newSequenceId = newSequence.emailSequenceTemplate.id;
            if (!materializedSequenceIds.includes(newSequenceId)) {
                setMaterializedSequenceIds(materializedSequenceIds.concat(newSequenceId));
                setReplyTemplateIds(getReplyMessageTemplateIds(newSequence));
                setTimeout(() => {
                    onSequenceMaterialize(newSequence);
                }, 1);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [materializedSequenceTemplate, props.selectedSequenceId]);

    const groupsFromFormState: TemplateGroup[] = useSelector((state: NutshellState) =>
        valueSelector(state, 'templateGroups')
    );
    const groupTemplatesFromFormState = groupsFromFormState
        ? getGroupSequenceTemplates(groupsFromFormState)
        : undefined;
    const uncategorizedTemplatesFromFormState: TemplateGroup[] = useSelector(
        (state: NutshellState) => valueSelector(state, 'uncategorizedTemplates')
    );
    const shouldAutoFocusSubject =
        !groupTemplatesFromFormState ||
        !uncategorizedTemplatesFromFormState ||
        (groupTemplatesFromFormState && groupTemplatesFromFormState.length > 0) ||
        (uncategorizedTemplatesFromFormState && uncategorizedTemplatesFromFormState.length > 0);

    /*
     * Get the plain text from react-quill's editor, so that we can show a plaintext
     * body in the template list.
     */
    const handleBodyChange =
        // @ts-expect-error


            ({onChange}) =>
            // @ts-expect-error
            (newHtml, newText) => {
                onChange(newHtml);

                props.onChangeBody({
                    plaintext: newText,
                    html: newHtml,
                });
            };

    const relatedEntity = props.emails.filter((email) => email.entity);
    // @ts-expect-error
    const relatedEntityName = relatedEntity.length ? relatedEntity[0].entity.name : null;
    const relatedLead = props.relatedLeads.length ? props.relatedLeads[0] : null;

    return (
        <EmailRelatedLeadContext.Provider value={relatedLead}>
            <div className={styles['email-composer-form']}>
                <Field name='fromAddress' user={props.user} component={FromField} />
                {isReply ? (
                    <FormSection name='reply'>
                        <div className={styles['reply-container']}>
                            {shouldShowReplyAllSwitch ? (
                                <Field name='isReplyAll' component={ReplyButtonField} />
                            ) : undefined}
                            <div
                                className={classnames(
                                    shouldShowReplyAllSwitch ? undefined : 'full-width',
                                    shouldShowReplyAllSwitch ? styles['reply-to-field'] : undefined
                                )}
                            >
                                {isReplyAll ? (
                                    <Field
                                        name='replyAllAddresses'
                                        emails={props.emails}
                                        component={RecipientsField}
                                        onShowCC={() => setShowCC(true)}
                                    />
                                ) : (
                                    <Field
                                        name='toAddresses'
                                        emails={props.emails}
                                        component={RecipientsField}
                                        onShowCC={() => setShowCC(true)}
                                    />
                                )}
                            </div>
                        </div>
                    </FormSection>
                ) : (
                    <Field
                        name='toAddresses'
                        emails={props.emails}
                        component={RecipientsField}
                        onShowCC={() => setShowCC(true)}
                    />
                )}
                {showCC ? (
                    <Field name='ccAddresses' emails={props.emails} component={RecipientsField} />
                ) : null}
                {props.relatedLeads.length ? (
                    <Field
                        name='relatedLeadIds'
                        relatedLeads={props.relatedLeads}
                        component={RelatedLeadsField}
                        relatedEntityName={relatedEntityName}
                    />
                ) : null}
                <div className={styles['composer-fields']}>
                    {templateIds.map((id) => {
                        const composerFieldName = getTemplateFormStateName({
                            sequenceId: props.selectedSequenceId,
                            templateId: id,
                        });

                        const sendAsReply = replyTemplateIds.includes(id);

                        return (
                            <FormSection name={composerFieldName} key={id}>
                                <EmailFormSection
                                    isMultiSequence={props.isMultiSequence}
                                    isActive={id === props.selectedTemplateId}
                                    isBodyFocused={props.isBodyFocused}
                                    sendAsReply={sendAsReply}
                                    onBodyChange={handleBodyChange}
                                    onSubjectChange={props.onChangeSubject}
                                    isLoading={isMaterializing}
                                    autoFocusSubject={shouldAutoFocusSubject}
                                    suggestedReplies={props.suggestedReplies}
                                    selectedSuggestedReplyId={props.selectedSuggestedReplyId}
                                    isLoadingSuggestedReplies={props.isLoadingSuggestedReplies}
                                    emailReplyId={props.emailReplyId}
                                />
                            </FormSection>
                        );
                    })}
                </div>
            </div>
        </EmailRelatedLeadContext.Provider>
    );
}
